import { useEffect, useRef } from 'preact/hooks';
import type { VoiceOption } from '../hooks/useTTS';

interface Props {
  open: boolean;
  voices: VoiceOption[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  onPreview: (id: string) => void;
  onClose: () => void;
}

export function VoicePicker({ open, voices, selectedId, onSelect, onPreview, onClose }: Props) {
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  // Scroll selected voice into view on open
  useEffect(() => {
    if (!open || !listRef.current) return;
    const active = listRef.current.querySelector('.voice-item.selected');
    if (active) active.scrollIntoView({ block: 'center' });
  }, [open]);

  if (!open) return null;

  return (
    <>
      <div class="drawer-overlay" onClick={onClose} />
      <div class="voice-picker">
        <div class="voice-picker-header">
          <span>Choose Voice</span>
          <button class="voice-picker-close" onClick={onClose}>&times;</button>
        </div>
        <div class="voice-picker-list" ref={listRef}>
          {voices.map((v) => {
            const isSelected = v.id === selectedId;
            return (
              <div
                key={v.id}
                class={`voice-item${isSelected ? ' selected' : ''}`}
                onClick={() => { onSelect(v.id); onClose(); }}
              >
                <div class="voice-item-info">
                  <span class="voice-item-name">{v.name}</span>
                  <span class="voice-item-lang">
                    {v.gender}{v.accent ? ` \u00b7 ${v.accent}` : ''}
                    {v.description ? ` \u2014 ${v.description}` : ''}
                  </span>
                </div>
                <button
                  class="voice-preview-btn"
                  onClick={(e) => { e.stopPropagation(); onPreview(v.id); }}
                  title="Preview"
                >
                  &#9654;
                </button>
              </div>
            );
          })}
          {voices.length === 0 && (
            <div class="voice-empty">Piper TTS is starting up or unavailable. Try again in a moment.</div>
          )}
        </div>
      </div>
    </>
  );
}
